import kenyaCountyCensus2019 from "../../data/kenyaCountyCensus2019.json";
import kenyaCountyCentroids from "../../data/kenyaCountyCentroids.json";
import kenyaCountyGoogleAds from "../../data/kenyaCountyGoogleAds.json";
import kenyaCountyFacilityAccess from "../../data/kenyaCountyFacilityAccess.json";
import kenyaCountyKdhs2022 from "../../data/kenyaCountyKdhs2022.json";

/** @type {Record<string, Record<string, unknown>>} */
const TABLE = kenyaCountyCensus2019;

/** @type {{ geometrySource: string, counties: Record<string, { lat: number, lng: number }> }} */
const CENTROIDS = kenyaCountyCentroids;

/** @type {{ counties: Record<string, { criterionId: string, googleAdsName: string, googleAdsCanonicalName: string }> }} */
const GOOGLE_ADS_COUNTIES = kenyaCountyGoogleAds;

/** @type {typeof kenyaCountyFacilityAccess} */
const FACILITY = kenyaCountyFacilityAccess;

/** @type {typeof kenyaCountyKdhs2022} */
const KDHS2022 = kenyaCountyKdhs2022;

/**
 * County-level snapshot from bundled KNBS 2019 census tables (open.africa).
 * Keys match `GTG_KENYA_COUNTIES` spellings in `@healthdesk/shared-utils`.
 *
 * @param {string} canonicalCountyName
 * @returns {({
 *   county: string,
 *   censusYear: number,
 *   totalPopulation: number,
 *   pctMale: number,
 *   pctFemale: number,
 *   intersexCount: number,
 *   landAreaSqKm: number|null,
 *   populationDensitySqKm: number|null,
 * } & Record<string, unknown>) | null}
 */
export function getKenyaCountyCensusSnapshot(canonicalCountyName) {
  const name =
    typeof canonicalCountyName === "string" ? canonicalCountyName.trim() : "";
  if (!name) return null;
  const row = TABLE[name];
  if (!row || typeof row !== "object") return null;
  return { county: name, ...row };
}

export const KENYA_COUNTY_CENSUS_VINTAGE_LABEL =
  "KNBS 2019 Kenya Population and Housing Census — county tables (open.africa / Volume I)";

/**
 * Approximate map marker position (WGS84) for a canonical county name.
 * @param {string} canonicalCountyName
 * @returns {{ lat: number, lng: number } | null}
 */
export function getKenyaCountyCentroid(canonicalCountyName) {
  const name =
    typeof canonicalCountyName === "string" ? canonicalCountyName.trim() : "";
  if (!name) return null;
  const row = CENTROIDS.counties[name];
  if (
    !row ||
    typeof row.lat !== "number" ||
    typeof row.lng !== "number" ||
    !Number.isFinite(row.lat) ||
    !Number.isFinite(row.lng)
  ) {
    return null;
  }
  return { lat: row.lat, lng: row.lng };
}

/** Attribution / method note for map UIs (GADM-derived centroids). */
export const KENYA_COUNTY_CENTROID_SOURCE_NOTE = CENTROIDS.geometrySource;

/**
 * English label aligned with Google Ads geo target "Name" (County, Kenya) for the canonical county key.
 * @param {string} canonicalCountyName
 * @returns {string}
 */
export function getKenyaCountyGoogleAdsDisplayName(canonicalCountyName) {
  const name =
    typeof canonicalCountyName === "string" ? canonicalCountyName.trim() : "";
  if (!name) return "";
  return GOOGLE_ADS_COUNTIES.counties[name]?.googleAdsName || name;
}

/** Shown in Kenya targeting UIs — county picker labels mirror Google Ads location names. */
export const KENYA_GOOGLE_ADS_COUNTY_LABEL_NOTE =
  "County names shown match Google Ads location targets (Active · County · KE) from the published geo targets CSV.";

/**
 * @param {string} canonicalCountyName
 * @returns {{ criterionId: string, googleAdsName: string, googleAdsCanonicalName: string } | null}
 */
export function getKenyaCountyGoogleAdsRow(canonicalCountyName) {
  const name =
    typeof canonicalCountyName === "string" ? canonicalCountyName.trim() : "";
  if (!name) return null;
  const row = GOOGLE_ADS_COUNTIES.counties[name];
  if (!row) return null;
  return { ...row };
}

/** Human-readable source line for facility registry UIs. */
export const KENYA_FACILITY_ACCESS_VINTAGE_LABEL = `Facility counts from Kenya MoH registry (KMHFL), HDX snapshot ${FACILITY.snapshotDate}; facilities per 100k uses KNBS 2019 county population.`;

export const KENYA_FACILITY_ACCESS_SOURCE_URL = FACILITY.sourceUrl;

/** KDHS GF57 county fact sheets — citation-style line (survey, not census). */
export const KENYA_KDHS_2022_VINTAGE_LABEL = `${KDHS2022.surveyYear} Kenya DHS county fact sheets (${KDHS2022.publicationId}, ${KDHS2022.publicationMonth}) — survey estimates; see footnotes on each sheet.`;

export const KENYA_KDHS_2022_PROGRAM_URL = KDHS2022.programUrl;

export const KENYA_KDHS_2022_DATA_TYPE_NOTE = KDHS2022.dataTypeNote;

/**
 * KMHFL-derived operational facility counts and ownership/type buckets (canonical county key).
 * @param {string} canonicalCountyName
 * @returns {Record<string, unknown> | null}
 */
export function getKenyaCountyFacilityAccessSnapshot(canonicalCountyName) {
  const name =
    typeof canonicalCountyName === "string" ? canonicalCountyName.trim() : "";
  if (!name) return null;
  const row = FACILITY.counties[name];
  if (!row || typeof row !== "object") return null;
  return { county: name, ...row };
}

/**
 * Indicators extracted from the 2022 KDHS GF57 PDF for this county (+ fact sheet URL).
 * @param {string} canonicalCountyName
 * @returns {Record<string, unknown> | null}
 */
export function getKenyaCountyKdhs2022Snapshot(canonicalCountyName) {
  const name =
    typeof canonicalCountyName === "string" ? canonicalCountyName.trim() : "";
  if (!name) return null;
  const row = KDHS2022.counties[name];
  if (!row || typeof row !== "object") return null;
  return { county: name, ...row };
}

/**
 * Merged county row for targeting charts: KNBS census + KMHFL + KDHS (numeric keys are chart-safe).
 * @param {string} canonicalCountyName
 * @returns {Record<string, unknown> | null}
 */
export function getKenyaCountyTargetingSnapshot(canonicalCountyName) {
  const census = getKenyaCountyCensusSnapshot(canonicalCountyName);
  if (!census) return null;
  const fac = FACILITY.counties[canonicalCountyName.trim()];
  const kd = KDHS2022.counties[canonicalCountyName.trim()];
  return {
    ...census,
    facilitiesOperational: fac?.facilitiesOperational ?? null,
    facilitiesPer100k: fac?.facilitiesPer100k ?? null,
    hospitals: fac?.hospitals ?? null,
    healthCentres: fac?.healthCentres ?? null,
    dispensariesClinics: fac?.dispensariesClinics ?? null,
    ownerPublicSector: fac?.ownerPublicSector ?? null,
    ownerPrivateForProfit: fac?.ownerPrivateForProfit ?? null,
    under5MortalityPer1000: kd?.under5MortalityPer1000County ?? null,
    neonatalMortalityPer1000: kd?.neonatalMortalityPer1000County ?? null,
    infantMortalityPer1000: kd?.infantMortalityPer1000County ?? null,
    skilledBirthPct: kd?.skilledBirthPctCounty ?? null,
    anc4PlusPct: kd?.anc4PlusPctCounty ?? null,
    kdhsFullVacc12to23mPct: kd?.fullVaccination12to23mPctCounty ?? null,
  };
}
